import type { JSX } from 'react';
import { useState } from 'react';
import type { SurfaceOf } from './types';
import { formatTime } from './format';
import { useSurfaceActions } from './context';
import { useT } from '../i18n/useT';

type Calendar = SurfaceOf<'calendar'>;
type Appt = Calendar['appointments'][number];

const STATUS_CLASS: Record<Appt['status'], string> = {
  PROPOSED: 'chip-muted',
  DRAFT: 'chip-muted',
  BOOKED: 'chip-info',
  CHECKED_IN: 'chip-warn',
  IN_PROGRESS: 'chip-good',
  COMPLETED: 'chip-muted',
  CANCELLED: 'chip-bad',
  NO_SHOW: 'chip-bad',
};

export function CalendarSurfaceView({ surface }: { surface: Calendar }): JSX.Element {
  const t = useT();
  const actions = useSurfaceActions();
  const [selected, setSelected] = useState<string | undefined>();
  const groups = new Map<string, Appt[]>();
  for (const a of [...surface.appointments].sort((x, y) => x.start.localeCompare(y.start))) {
    const key = a.providerName ?? a.providerId;
    const list = groups.get(key) ?? [];
    list.push(a);
    groups.set(key, list);
  }
  return (
    <section className="surface surface-calendar">
      <h3 className="surface-title">
        {surface.title} <span className="muted small">{surface.date}</span>
      </h3>
      {groups.size === 0 ? <p className="muted">{t('none')}</p> : null}
      {Array.from(groups.entries()).map(([provider, appts]) => (
        <div key={provider} className="calendar-provider">
          <h4>{provider}</h4>
          <ol className="timeline">
            {appts.map((a) => {
              const isSel = selected === a.appointmentId;
              return (
                <li key={a.appointmentId} className={isSel ? 'appt selected' : 'appt'}>
                  <button type="button" className="appt-main" aria-expanded={isSel} onClick={() => setSelected(isSel ? undefined : a.appointmentId)}>
                    <span className="appt-time">
                      {formatTime(a.start, surface.timeZone)}–{formatTime(a.end, surface.timeZone)}
                    </span>
                    <span className="appt-name">{a.patientName ?? a.patientId}</span>
                    <span className="muted small">{a.appointmentTypeCode}{a.reason ? ` · ${a.reason}` : ''}</span>
                    <span className={`chip ${STATUS_CLASS[a.status]}`}>{a.status.replace('_', ' ')}</span>
                  </button>
                  {isSel ? (
                    <div className="appt-actions">
                      {a.status === 'BOOKED' ? (
                        <button type="button" className="btn btn-small" onClick={() => actions.sendMessage(`check in appointment ${a.appointmentId}`)}>
                          {t('checkIn')}
                        </button>
                      ) : null}
                      {a.status === 'BOOKED' || a.status === 'CHECKED_IN' ? (
                        <button type="button" className="btn btn-small" onClick={() => actions.sendMessage(`start encounter for appointment ${a.appointmentId}`)}>
                          {t('startEncounter')}
                        </button>
                      ) : null}
                      <span className="muted small">{a.appointmentId}</span>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ol>
        </div>
      ))}
    </section>
  );
}
